<template>
    <div v-if="visible" class="copy-container">
        <div class="copy-content" ref="target">
            <ButtonSecondary
                size="s"
                :onClick="onCopy"
                iconPosition="left"
                icon="Wand"
                class="copy-content-button">
                {{ t({ key: 'copy.flight.copy_to_all' }) }}
            </ButtonSecondary>

            <Toast v-if="message" icon="Check" color="green" padding="m">
                {{ message }}
            </Toast>
        </div>

        <Modal v-if="modal" :onClose="() => onSetModal(false)" size="s">
            <div class="copy-modal">
                <div class="copy-modal-content">
                    <p class="copy-modal-title">
                        {{ t({ key: 'copy.flight.copy_details' }) }}
                    </p>
                    <p class="copy-modal-description">
                        {{ t({ key: 'copy.flight.edit_later' }) }}
                    </p>
                    <p
                        v-if="hasSelectedSeating || hasSelectedBaggage"
                        class="copy-modal-description">
                        {{ t({ key: 'copy.flight.note_seating_baggage' }) }}
                    </p>
                </div>

                <div class="copy-modal-buttons">
                    <ButtonPrimary :onClick="onApply">
                        {{ t({ key: 'copy.flight.yes_copy' }) }}
                    </ButtonPrimary>
                    <ButtonText :onClick="() => onSetModal(false)">
                        {{ t({ key: 'cancellation.cancel' }) }}
                    </ButtonText>
                </div>
            </div>
        </Modal>
    </div>
</template>
<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import { Cart, FlightProduct, FlightPax, ProductType } from '@/api/Cart/cart.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { copyFlights, showCopyFlight } from '../Copy.helpers';
    import { useStore } from '@/store';
    import { submitFormById } from '@/helpers/formRegistry';
    import { useElementVisibility } from '@vueuse/core';
    import {
        logStatsigCheckoutCopyFeatureApplied,
        logStatsigCheckoutCopyFeatureClick,
        logStatsigCheckoutCopyFeatureInvalidForm,
    } from '@/tracking/statsig/checkout.functions';
    import { isNil } from 'lodash';
    import { getSelectedBaggages } from '@shared/helpers/baggage';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import ButtonText from '@ui/components/Button/ButtonText.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import Toast from '@ui/components/Toast/Toast.vue';

    export default defineComponent({
        name: 'CopyFlight',
        methods: { t },
        components: {
            ButtonPrimary,
            ButtonSecondary,
            ButtonText,
            Modal,
            Toast,
        },
        props: {
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
            formId: { type: String, required: true },
            formValid: { type: Boolean, required: true },
            formValues: { type: Object as PropType<FormValues | null> },
        },
        setup(props) {
            const modal = ref<boolean>(false);
            const message = ref<string | null>(null);

            const store = useStore();

            const target = ref(null);
            const targetIsVisible = useElementVisibility(target);

            const cart = computed<Cart>(() => store.state.cart.data);

            const visible = computed<boolean>(() =>
                showCopyFlight(props.product.Id, cart.value)
            );

            const hasSelectedSeating = computed<boolean>(() => {
                const selectedSeats = (props.product.Pax as FlightPax[])
                    .flatMap((p) => p.SelectedSeats)
                    .filter((s) => !isNil(s));

                return selectedSeats.length > 0;
            });

            const hasSelectedBaggage = computed<boolean>(() => {
                const selectedBaggage = (props.product.Pax as FlightPax[]).flatMap((p) =>
                    getSelectedBaggages(p.AdditionalBaggage)
                );

                return !isNil(selectedBaggage) && selectedBaggage.length > 0;
            });

            function onSetModal(state: boolean) {
                modal.value = state;
            }

            function onCopy() {
                logStatsigCheckoutCopyFeatureClick(ProductType.Flight, cart.value);

                if (props.formValid) {
                    onSetModal(true);
                } else {
                    submitFormById(props.formId, true);
                    logStatsigCheckoutCopyFeatureInvalidForm(ProductType.Flight, cart.value);
                }
            }

            function onApply() {
                const res = copyFlights(props.product.Id);

                if (res) {
                    message.value = t({
                        key: 'copy.flight.copied_to_all_flights',
                    });

                    onSetModal(false);

                    logStatsigCheckoutCopyFeatureApplied(ProductType.Flight, cart.value);
                }
            }

            watch(targetIsVisible, (visible) => {
                if (message.value && !visible) {
                    message.value = null;
                }
            });

            watch(
                () => props.formValues,
                () => {
                    if (message.value) {
                        message.value = null;
                    }
                },
                { immediate: true, deep: true }
            );

            return {
                target,
                visible,
                message,
                modal,
                onSetModal,
                onApply,
                onCopy,
                hasSelectedSeating,
                hasSelectedBaggage,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward '../Copy.scss';
</style>
